// src/components/forms/register/ProfileSection.jsx
import React, { useState } from 'react';
import { User, Users, Calendar, Globe, BookOpen, Home, Phone, Heart, Briefcase } from 'lucide-react';
import { InputField } from '../shared/InputField';
import { SelectField } from '../shared/SelectField';
import { GENDER_OPTIONS, YEAR_OPTIONS, MARITAL_STATUS_OPTIONS, SEM_OPTIONS } from '../../../constants/formOptions';

export const ProfileSection = ({ formData, setFormData }) => {
  // Determine whether the user is a student or staff
  const isStudent = formData.role === "student";
  const profilePath = isStudent ? formData.studentInfo.profile : formData.staffInfo.profile;

  const [errors, setErrors] = useState({
    fullName: '',
    gender: '',
    dateOfBirth: '',
    nationality: '',
    courseTaken: '',
    yearOfStudy: '',
    intakeSemester: '',
    enrollmentYear: '',
    position: '',
    department: '',
    residence: '',
    phone: ''
  });

  // Update profile fields for student or staff
  const updateProfile = (field, value) => {
    setFormData({
      ...formData,
      [isStudent ? "studentInfo" : "staffInfo"]: {
        ...formData[isStudent ? "studentInfo" : "staffInfo"],
        profile: {
          ...profilePath,
          [field]: value,
        },
      },
    });
  };
  
  // Update fields directly under root-level formData
  const updateRootField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Update fields directly under studentInfo or staffInfo
  const updateInfoField = (field, value) => {
    setFormData({
      ...formData,
      [isStudent ? "studentInfo" : "staffInfo"]: {
        ...formData[isStudent ? "studentInfo" : "staffInfo"],
        [field]: value,
      },
    });
  };

  // Validation functions
  const validateRequired = (field, value, label) => {
    if (!value) {
      setErrors(prev => ({ ...prev, [field]: `${label} is required` }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, [field]: '' }));
      return true;
    }
  };

  const validatePhone = (value) => {
    if (!value) {
      setErrors(prev => ({ ...prev, phone: 'Phone number is required' }));
      return false;
    } else if (!/^\+?[\d\s-]+$/.test(value)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
      return true;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
      <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Profile Information</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          id="fullName"
          label="Full Name"
          required
          icon={<User className="h-5 w-5 text-gray-400" />}
          value={formData.fullName}
          onChange={(e) => {
            updateRootField("fullName", e.target.value);
            if (errors.fullName) validateRequired('fullName', e.target.value, 'Full Name');
          }}
          validate={(value) => validateRequired('fullName', value, 'Full Name')}
          error={errors.fullName}
          placeholder="John Doe"
        />

        <SelectField
          id="gender"
          label="Gender"
          required
          icon={<Users className="h-5 w-5 text-gray-400" />}
          value={profilePath.gender}
          onChange={(e) => {
            updateProfile("gender", e.target.value);
            if (errors.gender) validateRequired('gender', e.target.value, 'Gender');
          }}
          validate={(value) => validateRequired('gender', value, 'Gender')}
          error={errors.gender}
          options={GENDER_OPTIONS}
        />

        <InputField
          id="dateOfBirth"
          label="Date of Birth"
          type="date"
          required
          icon={<Calendar className="h-5 w-5 text-gray-400" />}
          value={profilePath.dateOfBirth}
          onChange={(e) => {
            updateProfile("dateOfBirth", e.target.value);
            if (errors.dateOfBirth) validateRequired('dateOfBirth', e.target.value, 'Date of Birth');
          }}
          validate={(value) => validateRequired('dateOfBirth', value, 'Date of Birth')}
          error={errors.dateOfBirth}
        />

        <InputField
          id="nationality"
          label="Nationality"
          required
          icon={<Globe className="h-5 w-5 text-gray-400" />}
          value={profilePath.nationality}
          onChange={(e) => {
            updateProfile("nationality", e.target.value);
            if (errors.nationality) validateRequired('nationality', e.target.value, 'Nationality');
          }}
          validate={(value) => validateRequired('nationality', value, 'Nationality')}
          error={errors.nationality}
          placeholder="Kenyan"
        />

        {isStudent ? (
          <>
            {/* Student-Specific Fields */}
            <InputField
              id="courseTaken"
              label="Course Taken"
              required
              icon={<BookOpen className="h-5 w-5 text-gray-400" />}
              value={formData.studentInfo.courseTaken}
              onChange={(e) => {
                updateInfoField("courseTaken", e.target.value);
                if (errors.courseTaken) validateRequired('courseTaken', e.target.value, 'Course Taken');
              }}
              validate={(value) => validateRequired('courseTaken', value, 'Course Taken')}
              error={errors.courseTaken}
              placeholder="Bachelor of Science in Computer Science"
            />

            <SelectField
              id="yearOfStudy"
              label="Year of Study"
              required
              icon={<Calendar className="h-5 w-5 text-gray-400" />}
              value={formData.studentInfo.yearOfStudy}
              onChange={(e) => {
                updateInfoField("yearOfStudy", e.target.value);
                if (errors.yearOfStudy) validateRequired('yearOfStudy', e.target.value, 'Year of Study');
              }}
              validate={(value) => validateRequired('yearOfStudy', value, 'Year of Study')}
              error={errors.yearOfStudy}
              options={YEAR_OPTIONS}
            />

            <SelectField
              id="intakeSemester"
              label="Intake Semester"
              required
              icon={<Calendar className="h-5 w-5 text-gray-400" />}
              value={formData.studentInfo.intakeSemester}
              onChange={(e) => {
                updateInfoField("intakeSemester", e.target.value);
                if (errors.intakeSemester) validateRequired('intakeSemester', e.target.value, 'Intake Semester');
              }}
              validate={(value) => validateRequired('intakeSemester', value, 'Intake Semester')}
              error={errors.intakeSemester}
              options={SEM_OPTIONS}
            />

            <InputField
              id="enrollmentYear"
              label="Enrollment Year"
              required
              icon={<BookOpen className="h-5 w-5 text-gray-400" />}
              value={formData.studentInfo.enrollmentYear}
              onChange={(e) => {
                updateInfoField("enrollmentYear", e.target.value);
                if (errors.enrollmentYear) validateRequired('enrollmentYear', e.target.value, 'Enrollment Year');
              }}
              validate={(value) => {
                if (!value) {
                  setErrors(prev => ({ ...prev, enrollmentYear: 'Enrollment Year is required' }));
                  return false;
                } else if (!/^\d{4}$/.test(value)) {
                  setErrors(prev => ({ ...prev, enrollmentYear: 'Please enter a valid 4-digit year' }));
                  return false;
                } else {
                  setErrors(prev => ({ ...prev, enrollmentYear: '' }));
                  return true;
                }
              }}
              error={errors.enrollmentYear}
              placeholder="2024"
            />
          </>
        ) : (
          <>
            <InputField
              id="position"
              label="Position"
              required
              icon={<Briefcase className="h-5 w-5 text-gray-400" />}
              value={formData.staffInfo.position}
              onChange={(e) => {
                updateInfoField("position", e.target.value);
                if (errors.position) validateRequired('position', e.target.value, 'Position');
              }}
              validate={(value) => validateRequired('position', value, 'Position')}
              error={errors.position}
              placeholder="Senior Lecturer"
            />

            <InputField
              id="department"
              label="Department"
              required
              icon={<Briefcase className="h-5 w-5 text-gray-400" />}
              value={formData.staffInfo.department}
              onChange={(e) => {
                updateInfoField("department", e.target.value);
                if (errors.department) validateRequired('department', e.target.value, 'Department');
              }}
              validate={(value) => validateRequired('department', value, 'Department')}
              error={errors.department}
              placeholder="Department of Computer Science"
            />
          </>
        )}

        <InputField
          id="religion"
          label="Religion"
          icon={<Heart className="h-5 w-5 text-gray-400" />}
          value={profilePath.religion}
          onChange={(e) => updateProfile("religion", e.target.value)}
          placeholder="Christianity"
        />

        <InputField
          id="residence"
          label="Residence"
          required
          icon={<Home className="h-5 w-5 text-gray-400" />}
          value={profilePath.residence}
          onChange={(e) => {
            updateProfile("residence", e.target.value);
            if (errors.residence) validateRequired('residence', e.target.value, 'Residence');
          }}
          validate={(value) => validateRequired('residence', value, 'Residence')}
          error={errors.residence}
          placeholder="Nairobi, Kenya"
        />

        <InputField
          id="phone"
          label="Phone Number"
          type="tel"
          required
          icon={<Phone className="h-5 w-5 text-gray-400" />}
          value={formData.phone}
          onChange={(e) => {
            updateRootField("phone", e.target.value);
            if (errors.phone) validatePhone(e.target.value);
          }}
          validate={validatePhone}
          error={errors.phone}
          placeholder="+254712345678"
        />

        <SelectField
          id="maritalStatus"
          label="Marital Status"
          icon={<Users className="h-5 w-5 text-gray-400" />}
          value={profilePath.maritalStatus}
          onChange={(e) => updateProfile("maritalStatus", e.target.value)}
          options={MARITAL_STATUS_OPTIONS}
        />
      </div>
    </div>
  );
};

export default ProfileSection;

// export const ProfileSection = ({ formData, setFormData }) => {
//   // Determine whether the user is a student or staff
//   const isStudent = formData.role === "student";
//   const profilePath = isStudent ? formData.studentInfo.profile : formData.staffInfo.profile;

//   // Update profile fields for student or staff
//   const updateProfile = (field, value) => {
//     setFormData({
//       ...formData,
//       [isStudent ? "studentInfo" : "staffInfo"]: {
//         ...formData[isStudent ? "studentInfo" : "staffInfo"],
//         profile: {
//           ...profilePath,
//           [field]: value,
//         },
//       },
//     });
//   };
//   // Update fields directly under root-level formData
//   const updateRootField = (field, value) => {
//     setFormData({
//       ...formData,
//       [field]: value,
//     });
//   };

//   // Update fields directly under studentInfo or staffInfo
//   const updateInfoField = (field, value) => {
//     setFormData({
//       ...formData,
//       [isStudent ? "studentInfo" : "staffInfo"]: {
//         ...formData[isStudent ? "studentInfo" : "staffInfo"],
//         [field]: value,
//       },
//     });
//   };

//   return (
//     <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
//       <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Profile Information</h3>

//       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//         <InputField
//           id="fullName"
//           label="Full Name"
//           required
//           icon={<User className="h-5 w-5 text-gray-400" />}
//           value={formData.fullName}
//           onChange={(e) => updateRootField("fullName", e.target.value)}
//         />

//         <SelectField
//           id="gender"
//           label="Gender"
//           required
//           icon={<Users className="h-5 w-5 text-gray-400" />}
//           value={profilePath.gender}
//           onChange={(e) => updateProfile("gender", e.target.value)}
//           options={GENDER_OPTIONS}
//         />

//         <InputField
//           id="dateOfBirth"
//           label="Date of Birth"
//           type="date"
//           required
//           icon={<Calendar className="h-5 w-5 text-gray-400" />}
//           value={profilePath.dateOfBirth} // Ensure `dob` exists in profilePath state
//           onChange={(e) => updateProfile("dateOfBirth", e.target.value)}
//         />


//         <InputField
//           id="nationality"
//           label="Nationality"
//           required
//           icon={<Globe className="h-5 w-5 text-gray-400" />}
//           value={profilePath.nationality}
//           onChange={(e) => updateProfile("nationality", e.target.value)}
//         />

//         {isStudent ? (
//           <>
//             {/* Student-Specific Fields */}
//             {/* <InputField
//               id="studentId"
//               label="Student ID"
//               required
//               icon={<BookOpen className="h-5 w-5 text-gray-400" />}
//               value={formData.studentInfo.studentId}
//               onChange={(e) => updateInfoField("studentId", e.target.value)}
//             /> */}

//             <InputField
//               id="major"
//               label="Major"
//               required
//               icon={<BookOpen className="h-5 w-5 text-gray-400" />}
//               value={formData.studentInfo.major}
//               onChange={(e) => updateInfoField("major", e.target.value)}
//             />

//             <SelectField
//               id="yearOfStudy"
//               label="Year of Study"
//               required
//               icon={<Calendar className="h-5 w-5 text-gray-400" />}
//               value={formData.studentInfo.yearOfStudy}
//               onChange={(e) => updateInfoField("yearOfStudy", e.target.value)}
//               options={YEAR_OPTIONS}
//             />

//             <SelectField
//               id="intakeSemester"
//               label="Intake Semester"
//               required
//               icon={<Calendar className="h-5 w-5 text-gray-400" />}
//               value={formData.studentInfo.intakeSemester}
//               onChange={(e) => updateInfoField("intakeSemester", e.target.value)}
//               options={SEM_OPTIONS}
//             />

//             <InputField
//               id="enrollmentYear"
//               label="Enrollment Year"
//               required
//               icon={<BookOpen className="h-5 w-5 text-gray-400" />}
//               value={formData.studentInfo.enrollmentYear}
//               onChange={(e) => updateInfoField("enrollmentYear", e.target.value)}
//             />
//           </>
//         ) : (
//           <>

//             <InputField
//               id="position"
//               label="Position"
//               required
//               icon={<Briefcase className="h-5 w-5 text-gray-400" />}
//               value={formData.staffInfo.position}
//               onChange={(e) => updateInfoField("position", e.target.value)}
//             />

//             <InputField
//               id="department"
//               label="Department"
//               required
//               icon={<Briefcase className="h-5 w-5 text-gray-400" />}
//               value={formData.staffInfo.department}
//               onChange={(e) => updateInfoField("department", e.target.value)}
//             />
//           </>
//         )}

//         <InputField
//           id="religion"
//           label="Religion"
//           icon={<Heart className="h-5 w-5 text-gray-400" />}
//           value={profilePath.religion}
//           onChange={(e) => updateProfile("religion", e.target.value)}
//         />

//         <InputField
//           id="residence"
//           label="Residence"
//           required
//           icon={<Home className="h-5 w-5 text-gray-400" />}
//           value={profilePath.residence}
//           onChange={(e) => updateProfile("residence", e.target.value)}
//         />

//         <InputField
//           id="phone"
//           label="Phone Number"
//           type="tel"
//           required
//           icon={<Phone className="h-5 w-5 text-gray-400" />}
//           value={formData.phone}
//           onChange={(e) => updateRootField("phone", e.target.value)}
//         />

//         <SelectField
//           id="maritalStatus"
//           label="Marital Status"
//           icon={<Users className="h-5 w-5 text-gray-400" />}
//           value={profilePath.maritalStatus}
//           onChange={(e) => updateProfile("maritalStatus", e.target.value)}
//           options={MARITAL_STATUS_OPTIONS}
//         />
//       </div>
//     </div>
//   );
// };

// export default ProfileSection;
